import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  MiniMap,
  Controls,
  useReactFlow,
  type Node,
  type Edge,
  type NodeTypes,
  type EdgeTypes,
  type NodeChange,
  type NodePositionChange,
  applyNodeChanges,
  BackgroundVariant,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Wand2, RotateCcw } from 'lucide-react';

import { useEntrance } from '../context/entrance-context';
import { ZONE_TO_COLOR_REGION } from '../data/entrance-types';
import { AREA_COLORS } from '@/components/tracker/data/area-colors';
import { PAD_IDS } from '../data/vanilla-entrances';
import { ZoneNode, DEAD_END_VALUES } from './zone-node';
import { TrackedEdge, EdgeMarkerDefs } from './tracked-edge';
import { forceDirectedLayout } from './force-layout';
import type { EntranceCategory, EntranceConnection } from '../data/entrance-types';
import { ONE_WAY_ENTRANCE_IDS } from '../data/entrance-types';

const ONE_WAY_CATEGORIES: Set<EntranceCategory> = new Set(['warp', 'spawn', 'owl']);

const nodeTypes: NodeTypes = {
  zone: ZoneNode,
};

const edgeTypes: EdgeTypes = {
  tracked: TrackedEdge,
};

/**
 * Layout zones in columns grouped by region, inspired by OoT map.
 */
function computeDefaultPositions(
  byZone: Map<string, EntranceConnection[]>,
): Record<string, { x: number; y: number }> {
  const columns: { zones: string[] }[] = [
    { zones: ['KF', 'LW', 'LW Bridge', 'SFM'] },
    { zones: ['HF', 'LLR'] },
    { zones: ['MKT', 'MKT Bridge', 'HC', 'OGC'] },
    { zones: ['KAK', 'Potion Shop', 'GY'] },
    { zones: ['DMT', 'GC', 'DMC'] },
    { zones: ['ZR', 'ZD', 'ZF', 'LH'] },
    { zones: ['GV', 'GF', 'Wasteland', 'DCol'] },
    { zones: ['Warps', 'Spawns', 'Owls'] },
  ];

  const ZONE_WIDTH = 230;
  const COL_GAP = 40;
  const ZONE_GAP = 20;
  const HEADER_HEIGHT = 24;
  const ROW_HEIGHT = 18;

  const positions: Record<string, { x: number; y: number }> = {};
  let colX = 0;

  for (const col of columns) {
    let y = 0;
    let hasAny = false;

    for (const zone of col.zones) {
      const entrances = byZone.get(zone);
      if (!entrances || entrances.length === 0) continue;
      hasAny = true;

      positions[zone] = { x: colX, y };
      const zoneHeight = HEADER_HEIGHT + entrances.length * ROW_HEIGHT + 12;
      y += zoneHeight + ZONE_GAP;
    }

    if (hasAny) {
      colX += ZONE_WIDTH + COL_GAP;
    }
  }

  return positions;
}

const ZONE_LABELS: Record<string, string> = {
  KF: 'Kokiri Forest', LW: 'Lost Woods', 'LW Bridge': 'LW Bridge', SFM: 'Sacred Forest Meadow',
  HF: 'Hyrule Field', MKT: 'Market', 'MKT Bridge': 'Market Bridge', HC: 'Hyrule Castle', OGC: 'Ganons Castle',
  LLR: 'Lon Lon Ranch', KAK: 'Kakariko Village', 'Potion Shop': 'Potion Shop', GY: 'Graveyard',
  DMT: 'Death Mountain Trail', GC: 'Goron City', DMC: 'Death Mountain Crater',
  ZR: 'Zora River', ZD: 'Zoras Domain', ZF: 'Zoras Fountain', LH: 'Lake Hylia',
  GV: 'Gerudo Valley', GF: 'Gerudo Fortress', Wasteland: 'Haunted Wasteland',
  DCol: 'Desert Colossus',
  Warps: 'Warp Songs', Spawns: 'Spawns', Owls: 'Owl Flights',
};

export function EntranceGraph() {
  return (
    <ReactFlowProvider>
      <EntranceGraphInner />
    </ReactFlowProvider>
  );
}

const ZONE_WIDTH = 220;
const ROW_HEIGHT = 18;
const HEADER_HEIGHT = 24;

function EntranceGraphInner() {
  const { state, trackEntrance, untrackEntrance, toggleZoneReward, setShopData, setScrubsCount } = useEntrance();
  const reactFlow = useReactFlow();

  // Two-click linking state
  const [linkSourceId, setLinkSourceId] = useState<string | null>(null);

  // User-dragged positions override default layout
  const draggedPositions = useRef<Record<string, { x: number; y: number }>>({});

  // Escape to cancel linking
  useEffect(() => {
    if (!linkSourceId) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLinkSourceId(null);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [linkSourceId]);

  // Handle entrance click: two-click linking (bidirectional for overworld)
  const handleEntranceClick = useCallback((entranceId: string, vanillaTo: string, zone: string, label: string) => {
    if (linkSourceId === null) {
      setLinkSourceId(entranceId);
    } else if (linkSourceId === entranceId) {
      setLinkSourceId(null);
    } else {
      // Link source → destination (drives the edge)
      const isOneWay = ONE_WAY_ENTRANCE_IDS.has(linkSourceId);
      trackEntrance(linkSourceId, vanillaTo, `${zone} (${label})`, isOneWay ? null : entranceId, false);

      // Link destination → source (reverse: no edge created) — skip for one-way entrances
      if (!isOneWay) {
        const sourceConn = state.connections[linkSourceId];
        if (sourceConn) {
          trackEntrance(entranceId, sourceConn.vanillaTo, `${sourceConn.zone} (${sourceConn.label})`, linkSourceId, true);
        }
      }

      setLinkSourceId(null);
    }
  }, [linkSourceId, trackEntrance, state.connections]);

  // Build nodes and edges from state
  const { builtNodes, edges, byZone } = useMemo(() => {
    const builtNodes: Node[] = [];
    const edges: Edge[] = [];

    // Group connections by zone
    const byZone = new Map<string, EntranceConnection[]>();
    for (const conn of Object.values(state.connections)) {
      if (!state.visibleCategories.includes(conn.category)) continue;
      if (!state.visibleZones.includes(conn.zone)) continue;
      if (state.hideDone && conn.actualDestination === '✓ Done') continue;
      if (state.searchTerm) {
        const term = state.searchTerm.toLowerCase();
        const matches =
          conn.label.toLowerCase().includes(term) ||
          conn.zone.toLowerCase().includes(term) ||
          (conn.actualDestination?.toLowerCase().includes(term) ?? false);
        if (!matches) continue;
      }

      const list = byZone.get(conn.zone) ?? [];
      list.push(conn);
      byZone.set(conn.zone, list);
    }

    // Compute default positions, then override with user-dragged positions
    const defaultPositions = computeDefaultPositions(byZone);

    for (const [zone, entrances] of byZone) {
      const defaultPos = defaultPositions[zone];
      if (!defaultPos) continue;

      const nodeId = `zone-${zone}`;
      const pos = draggedPositions.current[nodeId] ?? defaultPos;
      const colorRegion = ZONE_TO_COLOR_REGION[zone] ?? 'Hyrule';
      const nonPadEntrances = entrances.filter(e => !PAD_IDS.has(e.id));
      const trackedCount = nonPadEntrances.filter(e => e.isTracked).length;
      const totalCount = nonPadEntrances.length;

      builtNodes.push({
        id: nodeId,
        type: 'zone',
        position: pos,
        data: {
          label: ZONE_LABELS[zone] ?? zone,
          zone,
          colorRegion,
          entrances,
          trackedCount,
          totalCount,
          onTrack: trackEntrance,
          onUntrack: untrackEntrance,
          linkSourceId,
          onEntranceClick: handleEntranceClick,
          zoneRewards: state.zoneRewards,
          onToggleZoneReward: toggleZoneReward,
          shopData: state.shopData,
          scrubsCount: state.scrubsCount,
          onSetShopData: setShopData,
          onSetScrubsCount: setScrubsCount,
        },
        draggable: true,
        selectable: false,
      });
    }

    // Create edges for tracked connections
    // vanillaToMap: fallback for destination picker (may have duplicates, last wins)
    const vanillaToMap = new Map<string, EntranceConnection>();
    // fromDisplayMap: precise lookup for two-click linked targets via actualFrom
    const fromDisplayMap = new Map<string, EntranceConnection>();
    for (const conn of Object.values(state.connections)) {
      vanillaToMap.set(conn.vanillaTo, conn);
      fromDisplayMap.set(`${conn.zone} (${conn.label})`, conn);
    }

    for (const conn of Object.values(state.connections)) {
      if (!conn.isTracked || !conn.actualDestination) continue;
      if (!state.visibleCategories.includes(conn.category)) continue;
      if (!state.visibleZones.includes(conn.zone)) continue;
      // Reverse side of a bidirectional link: no edge (source side drives it)
      if (conn.isReverse) continue;
      // Dead-end destinations don't create edges
      if (DEAD_END_VALUES.has(conn.actualDestination) && !conn.linkedTo) continue;

      const targetConn = (conn.linkedTo && state.connections[conn.linkedTo])
        || (conn.actualFrom && fromDisplayMap.get(conn.actualFrom))
        || vanillaToMap.get(conn.actualDestination);
      const targetZone = targetConn?.zone;

      if (targetZone && byZone.has(targetZone) && targetConn) {
        const isSelfEdge = conn.zone === targetZone;
        const isBidirectional = !!conn.linkedTo;

        edges.push({
          id: `edge-${conn.id}`,
          source: `zone-${conn.zone}`,
          sourceHandle: isSelfEdge ? `src-left-${conn.id}` : `src-${conn.id}`,
          target: `zone-${targetZone}`,
          targetHandle: `tgt-${targetConn.id}`,
          type: 'tracked',
          data: {
            category: conn.category,
            label: isBidirectional ? undefined : conn.label,
            isOneWay: ONE_WAY_CATEGORIES.has(conn.category),
            isBidirectional,
          },
          animated: false,
        });
      }
    }

    return { builtNodes, edges, byZone };
  }, [state.connections, state.visibleCategories, state.visibleZones, state.hideDone, state.searchTerm, state.zoneRewards, state.shopData, state.scrubsCount, trackEntrance, untrackEntrance, linkSourceId, handleEntranceClick, toggleZoneReward, setShopData, setScrubsCount]);

  // Controlled nodes state — seeded from builtNodes, updated by drag
  const [nodes, setNodes] = useState<Node[]>(builtNodes);

  // Sync when builtNodes change (filters, tracking, etc.) but preserve dragged positions
  useEffect(() => {
    setNodes(builtNodes.map(n => ({
      ...n,
      position: draggedPositions.current[n.id] ?? n.position,
    })));
  }, [builtNodes]);

  const onNodesChange = useCallback((changes: NodeChange[]) => {
    setNodes(prev => {
      const next = applyNodeChanges(changes, prev);
      for (const change of changes) {
        if (change.type === 'position' && (change as NodePositionChange).position) {
          const pos = (change as NodePositionChange).position;
          if (pos) {
            draggedPositions.current[change.id] = pos;
          }
        }
      }
      return next;
    });
  }, []);

  // Auto-layout: force-directed based on edges
  const handleAutoLayout = useCallback(() => {
    const layoutNodes = nodes.map(n => {
      const zone = n.id.replace('zone-', '');
      const entranceCount = byZone.get(zone)?.length ?? 3;
      return {
        id: n.id,
        x: n.position.x,
        y: n.position.y,
        width: ZONE_WIDTH,
        height: HEADER_HEIGHT + entranceCount * ROW_HEIGHT + 12,
      };
    });

    const layoutEdges = edges.map(e => ({ source: e.source, target: e.target }));
    const newPositions = forceDirectedLayout(layoutNodes, layoutEdges);

    // Apply new positions
    draggedPositions.current = { ...newPositions };
    setNodes(prev => prev.map(n => ({
      ...n,
      position: newPositions[n.id] ?? n.position,
    })));

    setTimeout(() => reactFlow.fitView({ padding: 0.1 }), 50);
  }, [nodes, edges, byZone, reactFlow]);

  // Reset layout: clear all dragged positions
  const handleResetLayout = useCallback(() => {
    draggedPositions.current = {};
    setNodes(builtNodes);
    setTimeout(() => reactFlow.fitView({ padding: 0.1 }), 50);
  }, [builtNodes, reactFlow]);

  const onInit = useCallback(() => {
    setTimeout(() => reactFlow.fitView({ padding: 0.1 }), 100);
  }, [reactFlow]);

  return (
    <div className="w-full h-full relative">
      <EdgeMarkerDefs />
      {/* Top toolbar */}
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
        <button
          onClick={handleAutoLayout}
          className="flex items-center gap-1 px-2 py-1 text-[11px] bg-gray-800/90 border border-gray-700 rounded text-gray-300 hover:text-zelda-gold hover:border-zelda-gold/40 backdrop-blur-sm transition-colors"
          title="Réorganiser les zones selon les liens"
        >
          <Wand2 size={12} />
          Auto-layout
        </button>
        <button
          onClick={handleResetLayout}
          className="flex items-center gap-1 px-2 py-1 text-[11px] bg-gray-800/90 border border-gray-700 rounded text-gray-300 hover:text-white backdrop-blur-sm transition-colors"
          title="Remettre le layout par défaut"
        >
          <RotateCcw size={12} />
          Reset
        </button>
      </div>
      {/* Linking mode banner */}
      {linkSourceId && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 bg-zelda-gold/20 border border-zelda-gold/40 text-zelda-gold text-xs px-3 py-1.5 rounded-lg backdrop-blur-sm">
          Cliquez sur la destination (ou Ctrl+Click) — <button onClick={() => setLinkSourceId(null)} className="underline hover:text-white">Annuler (Esc)</button>
        </div>
      )}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onInit={onInit}
        fitView
        minZoom={0.1}
        maxZoom={2}
        defaultViewport={{ x: 0, y: 0, zoom: 0.5 }}
        proOptions={{ hideAttribution: true }}
        className="bg-gray-950"
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#374151" />
        <MiniMap
          className="!bg-gray-900 !border-gray-700"
          nodeColor={(node) => {
            if (node.type === 'zone') {
              const region = (node.data as { colorRegion?: string }).colorRegion;
              return region ? (AREA_COLORS[region] ?? '#6b7280') : '#6b7280';
            }
            return 'transparent';
          }}
          maskColor="rgba(0,0,0,0.7)"
          pannable
          zoomable
        />
        <Controls className="!bg-gray-800 !border-gray-700 !shadow-lg [&>button]:!bg-gray-800 [&>button]:!border-gray-700 [&>button]:!text-gray-300 [&>button:hover]:!bg-gray-700" />
      </ReactFlow>
    </div>
  );
}
